import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { GridConfigContext, type GridAggregateDisplay } from './GridConfigProvider';
import { GRID_SETTING_KEYS } from './settings';
import { isDeviceScope, scopeIncludes, type DeviceScope } from '@/shared/device/device';
import { useDevice } from '@/shared/device/useDevice';

export type SummaryCardsPlacement = 'top' | 'topBelow' | 'bottom' | 'sidebar';

export interface SummaryCardsContextValue {
  /** Whether the cards show on this device: switched on, and on a device their scope includes. */
  readonly summaryCardsVisible: boolean;
  /** The devices the cards show on. */
  readonly summaryCardsDevice: DeviceScope;
  readonly setSummaryCardsDevice: (scope: DeviceScope) => void;
  readonly setSummaryCardsVisible: (visible: boolean) => void;
  readonly toggleSummaryCards: () => void;
  readonly hasSummaryCards: boolean;
  readonly setHasSummaryCards: (hasCards: boolean) => void;
  /** Whether the grid has at least one actual card (not just the add button). */
  readonly hasCardItems: boolean;
  readonly setHasCardItems: (has: boolean) => void;
  readonly summaryCardsPlacement: SummaryCardsPlacement;
  readonly setSummaryCardsPlacement: (placement: SummaryCardsPlacement) => void;
  /** The devices the page's special controls show on, and whether that includes this one. */
  readonly controlsDevice: DeviceScope;
  readonly setControlsDevice: (scope: DeviceScope) => void;
  readonly controlsOnHere: boolean;
  readonly controlsPlacement: SummaryCardsPlacement;
  readonly setControlsPlacement: (placement: SummaryCardsPlacement) => void;
  readonly addCardButtonVisible: boolean;
  readonly toggleAddCardButton: () => void;
  readonly totalsRowVisible: boolean;
  readonly setTotalsRowVisible: (visible: boolean) => void;
  readonly toggleTotalsRow: () => void;
  readonly groupSubtotalsVisible: boolean;
  readonly toggleGroupSubtotals: () => void;
  readonly groupSubtotalsPosition: 'top' | 'bottom';
  readonly setGroupSubtotalsPosition: (position: 'top' | 'bottom') => void;
  /** What the mounted grid shows before anyone picks: a pivot starts with subtotals below. */
  readonly setGroupSubtotalsDefault: (fallback: GroupSubtotalsDefault | null) => void;
  readonly groupBlankRows: boolean;
  readonly toggleGroupBlankRows: () => void;
  readonly groupMergeSingle: boolean;
  readonly toggleGroupMergeSingle: () => void;
  readonly groupOutline: boolean;
  readonly toggleGroupOutline: () => void;
  /** The grid (sheet) these options currently apply to; the mounted Grid registers it. */
  readonly setActiveGridKey: (gridKey: string | null) => void;
}

export interface GroupSubtotalsDefault { readonly visible: boolean; readonly position: 'top' | 'bottom'; readonly outline?: boolean }

const SummaryCardsContext = createContext<SummaryCardsContextValue | null>(null);

/**
 * The aggregate display options belong to each sheet, not to the page: they
 * are stored per gridKey in the shared grid settings, so everyone the document
 * is shared with sees the same cards, totals row and subtotals. Someone who
 * may not edit the shared settings still gets to flip them for themselves,
 * for this session only.
 */
export function SummaryCardsProvider({
  children,
  defaultVisible = false,
}: {
  readonly children: ReactNode;
  readonly defaultVisible?: boolean;
}) {
  const gridConfig = useContext(GridConfigContext);
  const [activeGridKey, setActiveGridKey] = useState<string | null>(null);
  const [localOverrides, setLocalOverrides] = useState<Record<string, GridAggregateDisplay>>({});
  const [hasSummaryCards, setHasSummaryCards] = useState<boolean>(false);
  const [hasCardItems, setHasCardItems] = useState<boolean>(true);
  const [subtotalsDefault, setSubtotalsDefault] = useState<GroupSubtotalsDefault | null>(null);

  const storageKey = activeGridKey ?? '';
  const stored = gridConfig?.aggregateDisplay[storageKey];
  const display: GridAggregateDisplay = { ...stored, ...localOverrides[storageKey] };

  const device = useDevice();
  const summaryCardsDevice: DeviceScope = isDeviceScope(display.cardsOn) ? display.cardsOn : 'all';
  const cardsOnHere = scopeIncludes(summaryCardsDevice, device);
  const summaryCardsVisible = (display.cardsVisible ?? defaultVisible) && cardsOnHere;
  const summaryCardsPlacement: SummaryCardsPlacement =
    display.cardsPlacement === 'topBelow' || display.cardsPlacement === 'bottom' || display.cardsPlacement === 'sidebar' ? display.cardsPlacement : 'top';
  const controlsDevice: DeviceScope = isDeviceScope(display.controlsOn) ? display.controlsOn : 'all';
  const controlsOnHere = scopeIncludes(controlsDevice, device);
  const controlsPlacement: SummaryCardsPlacement =
    display.controlsPlacement === 'topBelow' || display.controlsPlacement === 'bottom' || display.controlsPlacement === 'sidebar' ? display.controlsPlacement : 'top';
  // With no cards the add button is the only way to create one, so it stays on.
  const addCardButtonVisible = !hasCardItems || display.addCardButton !== false;
  const totalsRowVisible = display.totalsRow === true;
  const groupSubtotalsVisible = typeof display.groupSubtotals === 'boolean' ? display.groupSubtotals : subtotalsDefault?.visible ?? false;
  const groupSubtotalsPosition = display.groupSubtotalsPosition === 'bottom' || display.groupSubtotalsPosition === 'top'
    ? display.groupSubtotalsPosition : subtotalsDefault?.position ?? 'top';
  const groupBlankRows = display.groupBlankRows === true;
  const groupMergeSingle = display.groupMergeSingle === true;
  const groupOutline = typeof display.groupOutline === 'boolean' ? display.groupOutline : subtotalsDefault?.outline ?? false;

  const update = useCallback(
    (patch: GridAggregateDisplay) => {
      const canShare = Boolean(gridConfig?.canEditFormats && activeGridKey);
      if (gridConfig && activeGridKey && canShare) {
        const all = gridConfig.aggregateDisplay;
        void gridConfig.save(GRID_SETTING_KEYS.aggregateDisplay, {
          ...all,
          [activeGridKey]: { ...all[activeGridKey], ...patch },
        });
        return;
      }
      setLocalOverrides((current) => ({ ...current, [storageKey]: { ...current[storageKey], ...patch } }));
    },
    [gridConfig, activeGridKey, storageKey],
  );

  const value = useMemo<SummaryCardsContextValue>(
    () => ({
      summaryCardsVisible,
      setSummaryCardsVisible: (visible) => update({ cardsVisible: visible, ...(visible && !cardsOnHere ? { cardsOn: 'all' as const } : {}) }),
      // Switching them on where their scope leaves them out shows them everywhere again.
      toggleSummaryCards: () => update({ cardsVisible: !summaryCardsVisible, ...(!summaryCardsVisible && !cardsOnHere ? { cardsOn: 'all' as const } : {}) }),
      summaryCardsDevice,
      setSummaryCardsDevice: (scope) => update({ cardsOn: scope }),
      hasSummaryCards,
      setHasSummaryCards,
      summaryCardsPlacement,
      // Picking a placement while the cards are hidden shows them there.
      setSummaryCardsPlacement: (placement) => update({
        cardsPlacement: placement,
        ...(!summaryCardsVisible ? { cardsVisible: true, ...(!cardsOnHere ? { cardsOn: 'all' as const } : {}) } : {}),
      }),
      controlsDevice,
      setControlsDevice: (scope) => update({ controlsOn: scope }),
      controlsOnHere,
      controlsPlacement,
      setControlsPlacement: (placement) => update({ controlsPlacement: placement }),
      addCardButtonVisible,
      hasCardItems,
      setHasCardItems,
      toggleAddCardButton: () => { if (hasCardItems) update({ addCardButton: !addCardButtonVisible }); },
      totalsRowVisible,
      setTotalsRowVisible: (visible) => update({ totalsRow: visible }),
      toggleTotalsRow: () => update({ totalsRow: !totalsRowVisible }),
      groupSubtotalsVisible,
      toggleGroupSubtotals: () => update({ groupSubtotals: !groupSubtotalsVisible }),
      groupSubtotalsPosition,
      setGroupSubtotalsPosition: (position) => update({ groupSubtotalsPosition: position }),
      setGroupSubtotalsDefault: setSubtotalsDefault,
      groupBlankRows,
      toggleGroupBlankRows: () => update({ groupBlankRows: !groupBlankRows }),
      groupMergeSingle,
      toggleGroupMergeSingle: () => update({ groupMergeSingle: !groupMergeSingle }),
      groupOutline,
      toggleGroupOutline: () => update({ groupOutline: !groupOutline }),
      setActiveGridKey,
    }),
    [summaryCardsVisible, summaryCardsDevice, cardsOnHere, hasSummaryCards, summaryCardsPlacement, controlsDevice, controlsOnHere, controlsPlacement, addCardButtonVisible, hasCardItems, totalsRowVisible, groupSubtotalsVisible, groupSubtotalsPosition, subtotalsDefault, groupBlankRows, groupMergeSingle, groupOutline, update],
  );

  return (
    <SummaryCardsContext.Provider value={value}>
      {children}
    </SummaryCardsContext.Provider>
  );
}

export function useOptionalSummaryCards(): SummaryCardsContextValue | null {
  return useContext(SummaryCardsContext);
}
